import React, { useState } from "react";
import PublicShell from "@/components/PublicShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  LifeBuoy,
  Mail,
  Loader2,
  CheckCircle2,
  Send,
  ShieldCheck,
  Clock,
  BookOpen,
} from "lucide-react";

const CATEGORIES = [
  { value: "account", label: "Account & login" },
  { value: "kyc", label: "Identity verification (KYC)" },
  { value: "deposit", label: "Deposits" },
  { value: "withdrawal", label: "Withdrawals" },
  { value: "transfer", label: "Transfers" },
  { value: "crypto", label: "Crypto (MoonPay)" },
  { value: "security", label: "Security & fraud" },
  { value: "billing", label: "Billing & fees" },
  { value: "other", label: "Something else" },
];

export default function Help() {
  const { user } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [subject, setSubject] = useState("");
  const [category, setCategory] = useState("other");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(null); // { ticket_id }

  const submit = async () => {
    if (!name.trim()) return toast.error("Please enter your name.");
    if (!/.+@.+\..+/.test(email)) return toast.error("Please enter a valid email.");
    if (subject.trim().length < 3) return toast.error("Please add a short subject.");
    if (message.trim().length < 10)
      return toast.error("Please describe your issue in at least a sentence.");
    setLoading(true);
    try {
      const { data } = await api.post("/support/ticket", {
        name: name.trim(),
        email: email.trim(),
        subject: subject.trim(),
        message: message.trim(),
        category,
      });
      setSubmitted({ ticket_id: data.ticket_id });
      toast.success(`Ticket ${data.ticket_id} received.`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <PublicShell>
      <section className="max-w-5xl mx-auto px-6 md:px-10 py-16">
        <div className="text-eyebrow text-[#4F4D45]">Support</div>
        <div className="flex items-center gap-3 mt-2">
          <LifeBuoy className="w-8 h-8 text-[#1B3526]" />
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight">
            How can we help?
          </h1>
        </div>
        <p className="text-[#4F4D45] mt-4 max-w-2xl">
          Real humans read every message. We reply within one business day and
          prioritize anything security-related within an hour.
        </p>

        <div className="mt-12 grid lg:grid-cols-3 gap-10">
          {/* Form */}
          <div className="lg:col-span-2 rounded-2xl border border-[#D2CDBE] bg-white p-6 md:p-8">
            {submitted ? (
              <div
                className="text-center py-10"
                data-testid="help-submitted-view"
              >
                <CheckCircle2 className="w-14 h-14 text-[#1B3526] mx-auto" />
                <h2 className="font-display text-2xl mt-4">Message received</h2>
                <p className="text-[#4F4D45] mt-2 max-w-md mx-auto">
                  Ticket{" "}
                  <span className="font-mono text-[#1B3526]">
                    {submitted.ticket_id}
                  </span>{" "}
                  is in our queue. Check your email — we've also sent you a
                  confirmation.
                </p>
                <Button
                  onClick={() => {
                    setSubmitted(null);
                    setSubject("");
                    setMessage("");
                    setCategory("other");
                  }}
                  className="mt-6 bg-[#1B3526] hover:bg-[#0F2517] text-white"
                  data-testid="help-submit-another"
                >
                  Send another
                </Button>
              </div>
            ) : (
              <>
                <div className="text-eyebrow text-[#4F4D45]">Send us a message</div>
                <h2 className="font-display text-2xl md:text-3xl font-bold mt-1">
                  Open a support ticket
                </h2>
                <div className="mt-6 space-y-5">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs text-[#4F4D45]">
                        Your name
                      </Label>
                      <Input
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Ada Lovelace"
                        className="mt-1"
                        data-testid="help-input-name"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-[#4F4D45]">Email</Label>
                      <Input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@example.com"
                        className="mt-1"
                        data-testid="help-input-email"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <Label className="text-xs text-[#4F4D45]">Category</Label>
                      <Select value={category} onValueChange={setCategory}>
                        <SelectTrigger
                          className="mt-1"
                          data-testid="help-select-category"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {CATEGORIES.map((c) => (
                            <SelectItem
                              key={c.value}
                              value={c.value}
                              data-testid={`help-cat-${c.value}`}
                            >
                              {c.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="sm:col-span-2">
                      <Label className="text-xs text-[#4F4D45]">Subject</Label>
                      <Input
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        placeholder="Brief summary of your issue"
                        className="mt-1"
                        maxLength={140}
                        data-testid="help-input-subject"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <Label className="text-xs text-[#4F4D45]">
                        Describe your issue
                      </Label>
                      <Textarea
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        placeholder="Include steps, dates, transaction IDs — anything that helps us resolve it faster."
                        className="mt-1 min-h-[160px]"
                        maxLength={5000}
                        data-testid="help-input-message"
                      />
                      <div className="text-right text-xs text-[#4F4D45] mt-1">
                        {message.length} / 5000
                      </div>
                    </div>
                  </div>
                  <Button
                    onClick={submit}
                    disabled={loading}
                    className="w-full bg-[#1B3526] hover:bg-[#0F2517] text-white"
                    data-testid="help-submit"
                  >
                    {loading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" /> Submit ticket
                      </>
                    )}
                  </Button>
                </div>
              </>
            )}
          </div>

          {/* Sidebar */}
          <aside className="space-y-4">
            <SidebarBlock
              Icon={Mail}
              title="Direct email"
              body="Prefer to write directly? Reach us at"
              accent={
                <a
                  href="mailto:support@finvault.capital"
                  className="text-[#1B3526] underline font-medium"
                >
                  support@finvault.capital
                </a>
              }
            />
            <SidebarBlock
              Icon={ShieldCheck}
              title="Security issues"
              body="If you suspect fraud or unauthorized access, mark your ticket 'Security & fraud' — we respond within one hour."
            />
            <SidebarBlock
              Icon={Clock}
              title="Response time"
              body="Under 24 hours for most tickets. Security issues within 1 hour."
            />
            <SidebarBlock
              Icon={BookOpen}
              title="Read up first"
              body={
                <>
                  Many answers already exist in our{" "}
                  <a
                    href="/privacy"
                    className="text-[#1B3526] underline"
                  >
                    Privacy Policy
                  </a>
                  ,{" "}
                  <a href="/terms" className="text-[#1B3526] underline">
                    Terms
                  </a>
                  , and{" "}
                  <a href="/about" className="text-[#1B3526] underline">
                    About
                  </a>{" "}
                  pages.
                </>
              }
            />
          </aside>
        </div>
      </section>
    </PublicShell>
  );
}

function SidebarBlock({ Icon, title, body, accent }) {
  return (
    <div className="rounded-2xl border border-[#D2CDBE] bg-[#F0ECE0]/60 p-5">
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4 text-[#1B3526]" />
        <div className="font-display font-semibold text-[#0A0A08]">{title}</div>
      </div>
      <div className="text-sm text-[#4F4D45] mt-2 leading-relaxed">{body}</div>
      {accent && <div className="mt-2">{accent}</div>}
    </div>
  );
}
